import { CSSProperties, useEffect } from 'react';
import { Box, Link, styled, Tooltip, Typography } from '@material-ui/core';
import { Customer } from 'Services/Mongo/Types';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { Product, ProductInventory } from '@setvi/shared/interfaces';
import { getStockForCustomer } from 'pages/products/utils';
import { Variant } from '@material-ui/core/styles/createTypography';
import { Skeleton } from '@material-ui/lab';
import { useSingerLabel } from 'hooksv2';
import { isWarehouseStocked } from 'componentsV2/product/details/content/subpages/warehouse/columns-content/components/stocked';
import { isNonStocked } from '../products-list/useProductColumns/contents/stock-info';
import { useRealTimeInventory } from './useRealtimeInventory';

export enum StockColor {
  Green = '#23A566',
  Red = '#FA2020'
}

export const getStock = (
  inventory: Product['inventory'],
  selectedCustomer?: Customer
) => getStockForCustomer(inventory, selectedCustomer);

interface StockToReportArgs {
  inventory: Product['inventory'];
  isDropship?: boolean;
  stockInfo?: Product['stockInfo'];
  isStockProduct?: Product['isStockProduct'];
  selectedCustomer?: Customer;
}

/**
 * Whether there is nothing meaningful to say about a product's availability.
 * Two cases:
 *  - Sysco-style products with no stock information at all (not stocked in all
 *    warehouses and explicitly not a stock product).
 *  - Anything carrying the "Not Stocked" badge (DEV-541), for all clients.
 *
 * Dropship is excluded: it explains *why* a product isn't stocked, so it stays
 * visible even though such products are non-stocked by definition.
 *
 * Exported so callers that render their own chrome around `Stock` (a separator
 * dot, a divider) can drop that chrome too instead of leaving it dangling.
 */
export const hasNoStockToReport = ({
  inventory,
  isDropship,
  stockInfo,
  isStockProduct,
  selectedCustomer
}: StockToReportArgs) => {
  if (isDropship) return false;

  const stock = getStock(inventory, selectedCustomer);

  return (
    (stock === 0 &&
      stockInfo?.allWarehouses === false &&
      isStockProduct === false) ||
    (stock <= 0 && isNonStocked(stockInfo))
  );
};

const StyledLink = styled(Link)({
  wordBreak: 'break-word',
  lineHeight: '24px',
  textDecoration: 'none !important',
  display: 'flex',
  alignItems: 'center',
  gap: 2
});

interface StockProps {
  newDesign?: boolean;
  inventory: Product['inventory'];
  isDropship: boolean;
  variant?: Variant;
  selectedCustomer?: Customer;
  /**
   * Required for the non-stocked handling below — without it a "Not Stocked"
   * product falls through to the red out-of-stock label.
   */
  stockInfo?: Product['stockInfo'];
  isStockProduct?: Product['isStockProduct'];
  /**
   * What to render for a product with no availability to report (see
   * `noStockToReport`): a hyphen, which keeps table columns aligned, or nothing
   * at all, which suits detail views where a lone "-" reads as broken.
   */
  nonStockedDisplay?: 'hyphen' | 'hide';
  style?: CSSProperties;
  autoLoad?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  /**
   * @param id - The product id to fetch the realtime inventory
   * @param onStockRefresh - The function for update product
   * @description This is used to fetch the realtime inventory for the product
   */
  stockRefresh?: {
    id: string;
    onStockRefresh: (newStockValue: ProductInventory[]) => void;
  };
}

export const Stock = ({
  newDesign,
  inventory,
  isDropship,
  variant = 'body1',
  style = {},
  autoLoad,
  selectedCustomer,
  isStockProduct,
  stockInfo,
  nonStockedDisplay = 'hyphen',
  disabled,
  stockRefresh,
  onClick
}: StockProps) => {
  const { fetchRealtimeInventory, loading } = useRealTimeInventory();
  const { isSinger } = useSingerLabel();
  const inStockLabel = isSinger ? 'Available' : 'In stock';
  const outOfStockLabel = isSinger ? 'Not Available' : 'Out of stock';

  const handleFetchRealtimeInventory = async (id: string) => {
    const res = await fetchRealtimeInventory(id);
    Array.isArray(res) && stockRefresh?.onStockRefresh?.(res);
  };

  const stock = getStock(inventory, selectedCustomer);
  const inStock = stock > 0;

  const stockColor = inStock ? StockColor.Green : StockColor.Red;

  const oldStyle = {
    color: disabled ? '#AFBAC6' : stockColor,
    WordBreak: 'break-word',
    cursor: stockRefresh ? 'pointer' : 'default'
  };

  const newStyle = {
    // color: '#333D47',
    fontWeight: 600,
    fontSize: 14
  };

  useEffect(() => {
    if (autoLoad && stockRefresh) {
      handleFetchRealtimeInventory(stockRefresh.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <Skeleton width="90px" height="32px" />;

  // Dropship is checked first: it explains *why* a product isn't stocked, so
  // it stays visible even though such products are non-stocked by definition.
  if (isDropship)
    return (
      <Tooltip
        arrow
        title="It is not stocked in the distributor's warehouse; instead, it is shipped directly from the vendor.">
        <Link
          variant={variant}
          underline="always"
          color="primary"
          style={{ cursor: onClick ? 'pointer' : 'default', ...style }}
          onClick={e => {
            e.stopPropagation();
            onClick?.();
          }}>
          Drop ship
        </Link>
      </Tooltip>
    );

  // Both cases used to fall through to the red out-of-stock label wherever the
  // caller forgot to pass `stockInfo`, which is why it must always be supplied.
  if (
    hasNoStockToReport({
      inventory,
      isDropship,
      stockInfo,
      isStockProduct,
      selectedCustomer
    })
  ) {
    if (nonStockedDisplay === 'hide') return null;

    return (
      <Typography variant={variant} color="textSecondary" style={style}>
        -
      </Typography>
    );
  }

  return (
    <Tooltip
      arrow
      placement="top"
      style={{ width: 'fit-content' }}
      title={
        <Box p={1}>
          <Box pb={1}>
            <b>Available in Warehouse</b>
          </Box>
          <ul
            style={{
              padding: 0,
              margin: 0,
              listStyle: 'none'
            }}>
            {inventory?.length === 0 && (
              <li>No stock information available. </li>
            )}
            {inventory?.map(row => (
              <li key={row.warehouse?._id}>
                {isSinger &&
                  stockInfo &&
                  `${isWarehouseStocked(row, stockInfo) ? 'S' : 'N'} - `}
                {row.warehouse?.name} - {inStockLabel}: {row.availableOnHand}
              </li>
            ))}
          </ul>
        </Box>
      }>
      <StyledLink
        variant={variant}
        style={{
          ...(newDesign && newStyle),
          ...oldStyle,
          ...style
        }}
        onClick={e => {
          e.stopPropagation();
          stockRefresh
            ? handleFetchRealtimeInventory(stockRefresh.id)
            : onClick?.();
        }}>
        {inStock ? (
          <>
            {inStockLabel}: {stock}
            <InfoOutlinedIcon style={{ fontSize: 14 }} />
          </>
        ) : (
          outOfStockLabel
        )}
      </StyledLink>
    </Tooltip>
  );
};
